<template>
   <div data-cy="multi-choice-v1">
      <div v-for="choice in choicesWithAnswers" :key="choice.id">
         <CheckboxItem
            :id="choice.id"
            :label="choice.label"
            :name="`${props.block.id}-multi-choice`"
            :checked="choice.isSelected"
            @change="onChoiceChanged"
         ></CheckboxItem>
      </div>
   </div>
   <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
</template>

<script setup lang="ts">
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { AnswerResp } from "@/data/api-layer/answersApi";

export interface MultiChoiceBlockAnswerData {
   choices: {
      id: string;
      label: string;
      isExclusive?: boolean;
      isSelected?: boolean;
   }[];
}
export interface MultipleChoiceBlock extends FlowselBlock {
   data: MultiChoiceBlockAnswerData;
}
interface Props {
   block: MultipleChoiceBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// props
// -----------------------------------------
const { t: $t } = useI18n();
const emit = defineEmits(["update-answer", "update-valid"]);
const props = defineProps<Props>();

const state = reactive({
   currentAnswer: {
      choices: props.userBlockAnswer?.data?.choices || [],
   } as MultiChoiceBlockAnswerData,
});

// computed
// -----------------------------------------
const choicesWithAnswers = computed(() => {
   // return array with exclusive options last
   const sortByExclusive = (a, b) => {
      // a is exclusive, b isn't
      if (a.isExclusive === true && !b.isExclusive) {
         return 1;
      }
      // a isn't exclusive, b is
      if (!a.isExclusive && b.isExclusive === true) {
         return -1;
      }
      // exclusive status for a same as b
      return 0;
   };
   const sortedChoices = [...props.block.data.choices].sort(sortByExclusive);

   // add isSelected property to each choice
   return sortedChoices.map((choice) => {
      const userChoiceIdFound = state.currentAnswer.choices.find((userChoice) => userChoice.id === choice.id);
      return {
         ...choice,
         isSelected: userChoiceIdFound ? true : false,
      };
   });
});

const isAnswerValid = computed(() => {
   // There is no validation for this component except for (possibly) required
   if (props.block.isRequired && (!state.currentAnswer?.choices || state.currentAnswer.choices.length < 1))
      return false;
   else return true;
});

const errorList = computed(() => {
   if (!isAnswerValid.value)
      return [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("validation.choice.required"),
         },
      ];
   return [];
});

// methods
// -----------------------------------------
function onChoiceChanged({ newChoice, isChecked }) {
   // remove the choice from the array (safety net to prevent duplicates in the array)
   const choiceIdToRemove = state.currentAnswer.choices.findIndex((choice) => choice.id === newChoice.id);
   if (choiceIdToRemove !== -1) {
      state.currentAnswer.choices.splice(choiceIdToRemove, 1);
   }

   // add the choice to the array if it is checked
   if (isChecked) {
      state.currentAnswer.choices.push({
         id: newChoice.id,
         label: newChoice.label,
      });
   }

   // remove other choices if the new choice is exclusive
   const thisChoiceWithAnswer = choicesWithAnswers.value.find((choiceWAnswer) => choiceWAnswer.id === newChoice.id);

   if (thisChoiceWithAnswer.isExclusive) {
      const filteredChoices = state.currentAnswer.choices.filter((choice) => choice.id === newChoice.id);
      state.currentAnswer.choices = filteredChoices;
   }
   // user selected one of the non-exclusive choices > remove all user exclusive choices
   else {
      // get all exclusive ids
      const exclusiveIds = choicesWithAnswers.value.filter((choice) => choice.isExclusive).map((choice) => choice.id);
      // filter out any exclusive choices from the user choices
      const filteredChoices = state.currentAnswer.choices.filter((choice) => !exclusiveIds.includes(choice.id));
      state.currentAnswer.choices = filteredChoices;
   }

   processInputs();
}

function processInputs() {
   if (!isAnswerValid.value) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      const answerToSubmit = state.currentAnswer?.choices?.[0] ? state.currentAnswer : null;
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// This is needed on initialise because validity is being evaluated & answers updated on input
// so if you go back or already have an answer, it would not be automatically evaluated
processInputs();
</script>

<style scoped>
.multi-choice-item .multi-choice-item-bg input {
   accent-color: white;
}

.multi-choice-item .multi-choice-item-bg input:checked {
   background: var(--colour-brand-background);
   accent-color: white;
}

.multi-choice-item .multi-choice-item-bg input:checked + .multi-choice-item-label {
   background: var(--colour-brand-background);
   color: white;
}
</style>
